import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Hand, Radio, Cloud, Loader2 } from 'lucide-react';
import {
  SWATID_MODES,
  SWATID_MODE_LABELS,
  type SwatidOperationMode,
} from '@/constants/swatid';

const modeConfig: Record<
  SwatidOperationMode,
  { icon: typeof Hand; color: string; bg: string; border: string; description: string }
> = {
  manual: {
    icon: Hand,
    color: 'text-emerald-600',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/50',
    description: 'Relés controlados por interruptores físicos',
  },
  local: {
    icon: Radio,
    color: 'text-amber-600',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/50',
    description: 'Relés controlados por programaciones horarias del dispositivo',
  },
  remota: {
    icon: Cloud,
    color: 'text-blue-600',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/50',
    description: 'Relés controlados por comandos remotos (API)',
  },
};

interface SwatidModeSelectorProps {
  currentMode: SwatidOperationMode;
  onModeChange: (mode: SwatidOperationMode) => void;
  isLoading?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

export function SwatidModeSelector({
  currentMode,
  onModeChange,
  isLoading = false,
  disabled = false,
  size = 'md',
}: SwatidModeSelectorProps) {
  return (
    <div className="flex gap-1 p-1 rounded-lg bg-muted/50 border">
      {SWATID_MODES.map((mode) => {
        const config = modeConfig[mode];
        const Icon = config.icon;
        const isActive = currentMode === mode;

        return (
          <Tooltip key={mode}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled || isLoading}
                onClick={() => !isActive && onModeChange(mode)}
                className={cn(
                  'flex-1 gap-1.5 h-8 px-2',
                  size === 'sm' && 'h-7 text-xs px-1.5',
                  isActive && cn(config.bg, config.border, 'border', config.color)
                )}
              >
                {isLoading && isActive ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Icon className="w-3.5 h-3.5" />
                )}
                <span className="hidden sm:inline">{SWATID_MODE_LABELS[mode]}</span>
                <span className="sm:hidden">{SWATID_MODE_LABELS[mode].slice(0, 3)}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{config.description}</TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}
